// Price alerts — a token's market cap or price crossing a line, above or
// below, in USD or GRAM — kept in this browser.
//
// There is no price alert on the server (the backend's notification rules
// are the wallet tracker's, a minimum trade size per tracked wallet), so the
// alerts live here and are checked against the live prices the site already
// receives (lib/livePriceStore: the platform's trade tape, the open token
// page, the portfolio) — while wils.trade is open in a tab of this browser,
// and only then. The watcher is lib/priceAlertsWatcher.
//
// An alert's direction is set from where the value stands when it is set: a
// line above it is "rises above", one below "falls below" — so an alert is a
// crossing, never true the moment it is made (a line the value stands on is
// refused). It fires once, then waits to be armed again. A market-cap alert
// keeps the token's supply of that moment (market cap ÷ price) and watches
// the price times it.
//
// The storage is the truth and is checked on every read (tradePresetStore's
// pattern), and the same in every tab.

import { useSyncExternalStore } from 'react'
import { formatCompact, formatPriceAxiom } from './format'
import { type LivePriceEntry, livePriceUsd } from './livePriceStore'
import { canonicalTonAddress } from './tonAddress'

export const PRICE_ALERTS_KEY = 'wt:price-alerts:v1'

/** How many alerts the browser keeps, armed and fired together. */
export const ALERTS_MAX = 50

export type AlertMetric = 'mcap' | 'price'
export type AlertUnit = 'usd' | 'gram'
export type AlertDirection = 'above' | 'below'

export interface PriceAlert {
  id: string
  /** The token, canonical (EQ…). */
  master: string
  /** The pool its page was open on, to open it there again. */
  pool?: string
  ticker: string
  metric: AlertMetric
  unit: AlertUnit
  direction: AlertDirection
  /** The line, in the alert's unit. */
  target: number
  /** Tokens, for a market-cap alert: its market cap ÷ its price when set. */
  supply?: number
  /** When it was set or last armed, ms. A price known before is no crossing. */
  createdAt: number
  /** When it fired, ms; absent while it is armed. */
  firedAt?: number
  /** The value it fired at. */
  firedValue?: number
}

export interface PriceAlertsBox {
  alerts: readonly PriceAlert[]
  /** Also as a browser notification (the permission is asked when it is
   *  switched on, in the dialog). */
  browser: boolean
}

const EMPTY: PriceAlertsBox = { alerts: [], browser: false }
const ID = /^a-[a-z0-9-]{1,40}$/

const listeners = new Set<() => void>()
let seenRaw: string | null = null
let seenBox: PriceAlertsBox = EMPTY
let unsaved: { raw: string | null } | null = null

const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0

function master(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  try {
    return canonicalTonAddress(value.trim())
  } catch {
    return null
  }
}

type Shaped = Record<string, unknown> & {
  id: string
  metric: AlertMetric
  unit: AlertUnit
  direction: AlertDirection
  target: number
  createdAt: number
}

// what an alert must be to be one: its words from their sets, its line and
// its moment positive, and a supply when it watches a market cap
function shaped(f: Record<string, unknown>): f is Shaped {
  const words =
    typeof f.id === 'string' &&
    ID.test(f.id) &&
    (f.metric === 'mcap' || f.metric === 'price') &&
    (f.unit === 'usd' || f.unit === 'gram') &&
    (f.direction === 'above' || f.direction === 'below')
  return (
    words &&
    positive(f.target) &&
    positive(f.createdAt) &&
    (f.metric !== 'mcap' || positive(f.supply))
  )
}

function alertOf(row: unknown): PriceAlert | null {
  if (typeof row !== 'object' || row === null) return null
  const f = row as Record<string, unknown>
  const m = master(f.master)
  if (!m || !shaped(f)) return null
  const out: PriceAlert = {
    id: f.id,
    master: m,
    ticker: typeof f.ticker === 'string' ? f.ticker.slice(0, 32) : '',
    metric: f.metric,
    unit: f.unit,
    direction: f.direction,
    target: f.target,
    createdAt: f.createdAt,
  }
  const pool = master(f.pool)
  if (pool) out.pool = pool
  if (f.metric === 'mcap') out.supply = f.supply as number
  if (positive(f.firedAt)) {
    out.firedAt = f.firedAt
    if (positive(f.firedValue)) out.firedValue = f.firedValue
  }
  return out
}

function checked(saved: unknown): PriceAlertsBox {
  if (typeof saved !== 'object' || saved === null) return EMPTY
  const box = saved as { v?: unknown; alerts?: unknown; browser?: unknown }
  if (box.v !== 1 || !Array.isArray(box.alerts)) return EMPTY
  const alerts: PriceAlert[] = []
  const ids = new Set<string>()
  for (const row of box.alerts) {
    const a = alertOf(row)
    if (!a || ids.has(a.id)) continue
    ids.add(a.id)
    alerts.push(a)
    if (alerts.length === ALERTS_MAX) break
  }
  return { alerts, browser: box.browser === true }
}

function parsed(raw: string | null): PriceAlertsBox {
  if (raw === null) return EMPTY
  try {
    return checked(JSON.parse(raw))
  } catch {
    return EMPTY
  }
}

function storedRaw(): string | null {
  if (unsaved) return unsaved.raw
  try {
    return localStorage.getItem(PRICE_ALERTS_KEY)
  } catch {
    return null
  }
}

function store(box: PriceAlertsBox) {
  const empty = box.alerts.length === 0 && !box.browser
  const raw = empty ? null : JSON.stringify({ v: 1, ...box })
  try {
    if (raw === null) localStorage.removeItem(PRICE_ALERTS_KEY)
    else localStorage.setItem(PRICE_ALERTS_KEY, raw)
    unsaved = null
  } catch {
    unsaved = { raw }
  }
  for (const listener of [...listeners]) listener()
}

export function readPriceAlerts(): PriceAlertsBox {
  const raw = storedRaw()
  if (raw !== seenRaw) {
    seenRaw = raw
    seenBox = parsed(raw)
  }
  return seenBox
}

/** "1.5M", "250k", "$1,250,000", "0.0000042" → the number; null for anything
 *  that is not a positive number. */
export function parseTarget(text: string): number | null {
  const s = text
    .trim()
    .replace(/[$,\s]/g, '')
    .toLowerCase()
  const m = /^(\d+(?:\.\d+)?|\.\d+)([kmb])?$/.exec(s)
  if (!m) return null
  const mult = m[2] === 'k' ? 1e3 : m[2] === 'm' ? 1e6 : m[2] === 'b' ? 1e9 : 1
  const n = Number(m[1]) * mult
  return positive(n) ? n : null
}

export interface AlertInput {
  master: string
  pool?: string
  ticker: string
  metric: AlertMetric
  unit: AlertUnit
  target: number
  supply?: number
  /** The value now, in the alert's metric and unit; it sets the direction. */
  current: number | null
}

export type AlertResult = 'added' | 'full' | 'invalid' | 'no-now' | 'at-now'

// the alert an input makes, or why it makes none
function made(
  input: AlertInput,
  id: string,
  now: number
): { alert: PriceAlert } | { why: Exclude<AlertResult, 'added' | 'full'> } {
  const m = master(input.master)
  if (!m || !positive(input.target)) return { why: 'invalid' }
  if (input.metric === 'mcap' && !positive(input.supply)) return { why: 'invalid' }
  if (!positive(input.current)) return { why: 'no-now' }
  if (input.current === input.target) return { why: 'at-now' }
  const alert: PriceAlert = {
    id,
    master: m,
    ticker: input.ticker.slice(0, 32),
    metric: input.metric,
    unit: input.unit,
    direction: input.target > input.current ? 'above' : 'below',
    target: input.target,
    createdAt: now,
  }
  const pool = master(input.pool)
  if (pool) alert.pool = pool
  if (input.metric === 'mcap') alert.supply = input.supply
  return { alert }
}

function newId(now: number): string {
  return `a-${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

/** Sets an alert; its direction comes from where the value stands now. */
export function addPriceAlert(input: AlertInput, now = Date.now()): AlertResult {
  const box = readPriceAlerts()
  if (box.alerts.length >= ALERTS_MAX) return 'full'
  const r = made(input, newId(now), now)
  if ('why' in r) return r.why
  store({ ...box, alerts: [...box.alerts, r.alert] })
  return 'added'
}

/** Sets an alert's line again (and arms it): the same rules as a new one. */
export function updatePriceAlert(
  id: string,
  input: Omit<AlertInput, 'master' | 'ticker' | 'pool'>,
  now = Date.now()
): AlertResult {
  const box = readPriceAlerts()
  const old = box.alerts.find((a) => a.id === id)
  if (!old) return 'invalid'
  const r = made({ ...input, master: old.master, ticker: old.ticker, pool: old.pool }, id, now)
  if ('why' in r) return r.why
  store({ ...box, alerts: box.alerts.map((a) => (a.id === id ? r.alert : a)) })
  return 'added'
}

/** Arms a fired alert again, at its line — only while the value stands on the
 *  side it crosses from. */
export function rearmPriceAlert(id: string, current: number | null, now = Date.now()): AlertResult {
  const old = readPriceAlerts().alerts.find((a) => a.id === id)
  if (!old) return 'invalid'
  if (!positive(current)) return 'no-now'
  const before = old.direction === 'above' ? current < old.target : current > old.target
  if (!before) return 'at-now'
  const box = readPriceAlerts()
  store({
    ...box,
    alerts: box.alerts.map((a) =>
      a.id === id ? { ...a, createdAt: now, firedAt: undefined, firedValue: undefined } : a
    ),
  })
  return 'added'
}

export function removePriceAlert(id: string): void {
  const box = readPriceAlerts()
  if (!box.alerts.some((a) => a.id === id)) return
  store({ ...box, alerts: box.alerts.filter((a) => a.id !== id) })
}

export function setBrowserNotify(on: boolean): void {
  const box = readPriceAlerts()
  if (box.browser === on) return
  store({ ...box, browser: on })
}

/**
 * Marks an armed alert fired, read fresh from the storage: true when this
 * call fired it, false when it was fired already (another tab was first) or
 * is gone. The watcher announces only on true.
 */
export function markAlertFired(id: string, value: number, now = Date.now()): boolean {
  const box = readPriceAlerts()
  const a = box.alerts.find((x) => x.id === id)
  if (!a || a.firedAt !== undefined) return false
  store({
    ...box,
    alerts: box.alerts.map((x) => (x.id === id ? { ...x, firedAt: now, firedValue: value } : x)),
  })
  return true
}

/** The value an alert watches, from a live price: the price in its unit, or
 *  the market cap at the supply it was set with. */
export function alertValue(
  a: Pick<PriceAlert, 'metric' | 'unit' | 'supply'>,
  entry: LivePriceEntry
): number | null {
  const price = a.unit === 'usd' ? livePriceUsd(entry) : entry.priceTon
  if (!positive(price)) return null
  if (a.metric === 'price') return price
  return positive(a.supply) ? price * a.supply : null
}

/** The armed alerts of `token` a new live price has carried across their line. */
export function dueAlerts(
  alerts: readonly PriceAlert[],
  token: string,
  entry: LivePriceEntry
): { alert: PriceAlert; value: number }[] {
  const out: { alert: PriceAlert; value: number }[] = []
  for (const a of alerts) {
    if (a.master !== token || a.firedAt !== undefined) continue
    // a price known before the alert was set cannot have crossed after it
    if (entry.knownAsOfMs < a.createdAt) continue
    const value = alertValue(a, entry)
    if (value === null) continue
    const crossed = a.direction === 'above' ? value >= a.target : value <= a.target
    if (crossed) out.push({ alert: a, value })
  }
  return out
}

/** A value as the token bar writes it: "$1.25M", "$0.0₅39", "4.2K GRAM". */
export function formatAlertValue(metric: AlertMetric, unit: AlertUnit, v: number): string {
  const figure = metric === 'mcap' ? formatCompact(v) : formatPriceAxiom(v)
  return unit === 'usd' ? `$${figure}` : `${figure} GRAM`
}

/** "Market cap rises above $5.00K". */
export function alertCondition(
  a: Pick<PriceAlert, 'metric' | 'unit' | 'direction' | 'target'>
): string {
  const what = a.metric === 'mcap' ? 'Market cap' : 'Price'
  const how = a.direction === 'above' ? 'rises above' : 'falls below'
  return `${what} ${how} ${formatAlertValue(a.metric, a.unit, a.target)}`
}

/** What a fired alert says: "Market cap rose above $5.00K". */
export function alertFiredText(
  a: Pick<PriceAlert, 'metric' | 'unit' | 'direction' | 'target'>
): string {
  const what = a.metric === 'mcap' ? 'market cap' : 'price'
  const how = a.direction === 'above' ? 'rose above' : 'fell below'
  return `${what} ${how} ${formatAlertValue(a.metric, a.unit, a.target)}`
}

function onStorage(event: StorageEvent) {
  if (event.key !== null && event.key !== PRICE_ALERTS_KEY) return
  for (const listener of [...listeners]) listener()
}

export function subscribePriceAlerts(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}

export function usePriceAlerts(): PriceAlertsBox {
  return useSyncExternalStore(subscribePriceAlerts, readPriceAlerts)
}
